import React, { createContext, useContext } from 'react';
import { workTaskService } from '../services/TaskService';

/**
 * Which task collection the surrounding task UI is operating on.
 *
 * The Work task module (WorkTasksModule + TaskDetail + TaskForm) was written
 * against `teacherTasks` by importing TaskService's functions at module level.
 * That is exactly what stops it being reusable: a module-level import is a
 * hardcoded collection, and the Personal space needs the same components bound
 * to `personalTasks` instead.
 *
 * THE DEFAULT IS THE WORK SERVICE, and that is the safety property that makes
 * this change boring. A component rendered with NO provider around it resolves
 * to precisely the service it used to import, so every existing mount —
 * /portal/work, the teacher dashboard, the admin home — behaves identically
 * without being touched. Only a surface that deliberately wraps itself in a
 * provider gets different data.
 *
 * WHAT DOES *NOT* BELONG HERE: `taskChangeState`. It is a pure helper that
 * touches no collection, so components keep importing it directly — routing it
 * through the context would imply it varies per space, which it does not.
 *
 * The default value is a module-level constant, so the context value is
 * referentially stable across renders. Effects that depend on these functions
 * therefore never re-fire because of the context itself.
 */

/* The COMPLETE Work instance, not TaskService's default export. That default is
   deliberately a 23-key subset — it omits subscribeToMyTaskReads and
   markTaskRead, which are named-exports-only and which WorkTasksModule consumes.
   Using it here would hand the Work default a different shape from every other
   binding and break the board at runtime with markTaskRead undefined. */
const TaskServiceContext = createContext(workTaskService);

/**
 * Bind a subtree of task UI to a specific space's service.
 *
 * @param {object} props.service a service from createTaskService (e.g.
 *   personalTaskService). Omit to inherit the Work default.
 */
export function TaskServiceProvider({ service, children }) {
	return (
		<TaskServiceContext.Provider value={service || workTaskService}>
			{children}
		</TaskServiceContext.Provider>
	);
}

/** The task service for the current surface. Defaults to Work. */
export function useTaskService() {
	return useContext(TaskServiceContext);
}

export { workTaskService as defaultTaskService };
export default TaskServiceContext;
